import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  EdgeChange,
  MarkerType,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type FinalConnectionState,
  type Node,
  type NodeChange,
  type NodePositionChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileImage,
  FileText,
  Hand,
  History,
  Link2,
  Loader2,
  Maximize2,
  MousePointer2,
  Save,
  Search,
  Share2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { HistoricoDrawer } from '@/components/fluxograma/HistoricoDrawer';
import { exportElementAsPdf, exportElementAsPng } from '@/lib/exportCanvas';

import { fluxogramaNodesService, fluxogramasService } from '@/services/fluxogramaApi';
import { usuarioService } from '@/services/api';
import { departamentoService, organogramaService } from '@/services/api';
import { projetotService } from '@/services/projetotApi';
import type {
  Fluxograma,
  FluxogramaEdge as ApiEdge,
  FluxogramaNode as ApiNode,
  FluxogramaShape,
  FluxogramaPermissao,
  SnapshotEdge,
  SnapshotNode,
} from '@/types/fluxograma';
import type { Usuario } from '@/types';
import type { ShapeNodeData } from '@/components/fluxograma/ShapeNode';
import { ShapeNode } from '@/components/fluxograma/ShapeNode';
import { ShapesPalette } from '@/components/fluxograma/ShapesPalette';
import { SHAPE_META } from '@/components/fluxograma/shapeMeta';
import {
  NodePropertiesPanel,
  type PanelDepartamento,
  type PanelPessoa,
  type PanelProjeto,
} from '@/components/fluxograma/NodePropertiesPanel';
import { EdgePropertiesPanel } from '@/components/fluxograma/EdgePropertiesPanel';
import { NodeQuickToolbar } from '@/components/fluxograma/NodeQuickToolbar';
import {
  CanvasContextMenu,
  type CtxItem,
  type CtxTarget,
} from '@/components/fluxograma/CanvasContextMenu';
import { applyAutoLayout } from '@/components/fluxograma/autoLayout';
import { AlignmentGuides } from '@/components/fluxograma/AlignmentGuides';
import { DragGhost } from '@/components/fluxograma/DragGhost';
import { getHelperLines } from '@/components/fluxograma/helperLines';
import {
  FluxogramaToolbar,
  type AlignDirection,
} from '@/components/fluxograma/FluxogramaToolbar';
import { useUndoRedo } from '@/hooks/useUndoRedo';
import { usePainelRecolhivel } from '@/hooks/usePainelRecolhivel';
import { alvoNoPonto, centroAbsoluto, ladosDaSeta, retangulosAbsolutos, type Retangulo } from '@/lib/fluxogramaLigacao';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

const tipoLabel: Record<Fluxograma['tipo'], string> = {
  processo: 'Processo',
  bpmn: 'BPMN',
  aprovacao: 'Aprovação',
  customizado: 'Customizado',
};

const nodeTypes = { shape: ShapeNode };

const AUTOSAVE_DELAY_MS = 1500;
/** Tempo segurando um item em cima de outro até aparecer "Solte para ligar com seta". */
const LIGAR_SEGURANDO_MS = 500;

/** Destaque do "ligar arrastando" por `data-id` (sem recriar a lista de nós do ReactFlow a cada quadro). */
const estiloLigacao = (alvoId: string, arrastadoId: string, seloEmBaixo: boolean) => {
  const esc = (v: string) => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : v);
  const alvo = `.react-flow__node[data-id="${esc(alvoId)}"]`;
  const arrastado = `.react-flow__node[data-id="${esc(arrastadoId)}"]`;
  return `${alvo}{outline:3px dashed hsl(var(--primary));outline-offset:6px;border-radius:10px}
${alvo}::after{content:'Solte para ligar com seta';position:absolute;left:50%;${seloEmBaixo ? 'top:calc(100% + 14px)' : 'top:-38px'};transform:translateX(-50%);white-space:nowrap;padding:3px 10px;border-radius:9999px;background:hsl(var(--primary));color:hsl(var(--primary-foreground));font-size:12px;font-weight:600;box-shadow:0 2px 8px rgb(0 0 0 / 0.25);pointer-events:none;z-index:10}
${arrastado}{opacity:0.4}`;
};

/** Ids que o banco deu às formas/setas novas (`id_map` do salvamento) + o que ele devolveu de cada uma. */
interface RespostaSalvamento {
  ids: { nos: Record<string, string>; setas: Record<string, string> };
  nos: Map<string, ApiNode>;
  setas: Map<string, ApiEdge>;
}

/**
 * Aplica a resposta do salvamento no estado LOCAL, sem substituí-lo: troca ids provisórios pelos do banco
 * (inclusive em `parentId`) e preenche `apiNode` de quem ainda não tinha. O resto do nó fica como está, para não
 * perder o que foi editado enquanto o salvamento estava em andamento (nem a seleção).
 */
const aplicarIdsNosNos = (lista: Node[], r: RespostaSalvamento): Node[] => {
  let mudou = false;
  const resultado = lista.map((n) => {
    const novoId = r.ids.nos[n.id] ?? n.id;
    const novoPai = n.parentId ? r.ids.nos[n.parentId] ?? n.parentId : undefined;
    const d = n.data as ShapeNodeData;
    const doBanco = r.nos.get(novoId);
    const apiNode = d.apiNode ? (d.apiNode.id === novoId ? d.apiNode : { ...d.apiNode, id: novoId }) : doBanco;
    if (novoId === n.id && novoPai === n.parentId && apiNode === d.apiNode) return n;
    mudou = true;
    return { ...n, id: novoId, ...(novoPai ? { parentId: novoPai } : {}), data: { ...d, apiNode } };
  });
  return mudou ? resultado : lista;
};

const aplicarIdsNasSetas = (lista: Edge[], r: RespostaSalvamento): Edge[] => {
  let mudou = false;
  const resultado = lista.map((e) => {
    const novoId = r.ids.setas[e.id] ?? e.id;
    const source = r.ids.nos[e.source] ?? e.source;
    const target = r.ids.nos[e.target] ?? e.target;
    const atual = (e.data as { apiEdge?: ApiEdge } | undefined)?.apiEdge;
    const apiEdge = atual ? (atual.id === novoId ? atual : { ...atual, id: novoId }) : r.setas.get(novoId);
    if (novoId === e.id && source === e.source && target === e.target && apiEdge === atual) return e;
    mudou = true;
    return { ...e, id: novoId, source, target, data: { ...(e.data ?? {}), apiEdge } };
  });
  return mudou ? resultado : lista;
};

const cuid = () => `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

const CLIPBOARD_KEY = 'fluxograma:clipboard';
const CHAVE_PALETA_RECOLHIDA = 'kora:fluxograma:paleta-recolhida';

interface ClipboardNode {
  client_id: string;
  shape: FluxogramaShape;
  label: string;
  descricao: string | null;
  width: number;
  height: number;
  style: ApiNode['style'];
  position_x: number;
  position_y: number;
  /** client_id do container/raia pai, só quando o pai também foi copiado junto. */
  parent_client_id: string | null;
}

interface ClipboardEdge {
  source: string;
  target: string;
  sourceHandle: string | null;
  targetHandle: string | null;
  apiEdge: ApiEdge | null;
}

interface ClipboardPayload {
  nodes: ClipboardNode[];
  edges: ClipboardEdge[];
}

const buildNodeData = (
  apiNode: ApiNode,
  pessoas: PanelPessoa[],
  departamentos: PanelDepartamento[],
): ShapeNodeData => ({
  label: apiNode.label,
  shape: apiNode.shape,
  width: apiNode.width,
  height: apiNode.height,
  style: apiNode.style,
  descricao: apiNode.descricao,
  responsavelLabel: apiNode.responsavel_node_id
    ? pessoas.find((p) => p.id === apiNode.responsavel_node_id)?.nome ?? null
    : null,
  departamentoLabel: apiNode.departamento_id
    ? departamentos.find((d) => d.id === apiNode.departamento_id)?.nome ?? null
    : null,
  apiNode,
});

const apiNodeToFlow = (
  n: ApiNode,
  pessoas: PanelPessoa[],
  departamentos: PanelDepartamento[],
): Node => ({
  id: n.id,
  type: 'shape',
  position: { x: n.position_x, y: n.position_y },
  data: buildNodeData(n, pessoas, departamentos),
  width: n.width,
  height: n.height,
  style: { width: n.width, height: n.height },
  ...(n.parent_id ? { parentId: n.parent_id } : {}),
});

// Formas que agrupam outros nós (container e raia funcionam como pai no React Flow).
const isContainerShape = (shape: FluxogramaShape | undefined) =>
  shape === 'container' || shape === 'swimlane';

// React Flow exige que o nó pai (container) apareça ANTES dos filhos no array.
const orderContainersFirst = (list: Node[]): Node[] => {
  const isContainer = (n: Node) => isContainerShape((n.data as ShapeNodeData)?.shape);
  return [...list.filter(isContainer), ...list.filter((n) => !isContainer(n))];
};

// Posição absoluta de um nó (soma a posição de todos os containers ancestrais,
// já que `position` do React Flow é relativa ao pai quando `parentId` existe).
const absolutePositionOf = (node: Node, byId: Map<string, Node>): { x: number; y: number } => {
  let x = node.position.x;
  let y = node.position.y;
  let current = node;
  while (current.parentId) {
    const parent = byId.get(current.parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    current = parent;
  }
  return { x, y };
};

// Marcador de seta. IMPORTANTE: quando não há cor, OMITIR a chave `color` —
// passar `color: undefined` faz o React Flow renderizar a seta com fill inválido
// (invisível). Sem a chave, ele usa a cor padrão e a seta aparece.
const arrowMarker = (color?: string | null) =>
  color ? { type: MarkerType.ArrowClosed, color } : { type: MarkerType.ArrowClosed };

const apiEdgeToFlow = (e: ApiEdge): Edge => ({
  id: e.id,
  source: e.source_node_id,
  target: e.target_node_id,
  sourceHandle: e.source_handle ?? undefined,
  targetHandle: e.target_handle ? `t-${e.target_handle}` : undefined,
  label: e.label ?? undefined,
  type: e.style_curva === 'step' ? 'step' : e.style_curva === 'straight' ? 'straight' : 'default',
  style: {
    strokeDasharray: e.tipo === 'dashed' ? '6 4' : e.tipo === 'dotted' ? '2 4' : undefined,
    stroke: e.color ?? undefined,
  },
  markerEnd: e.arrow_end ? arrowMarker(e.color) : undefined,
  markerStart: e.arrow_start ? arrowMarker(e.color) : undefined,
  data: { apiEdge: e },
});

function FluxogramaEditorInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, empresaAtiva } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';

  const [fluxograma, setFluxograma] = useState<Fluxograma | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [permissoesOpen, setPermissoesOpen] = useState(false);
  const [permissoesLoading, setPermissoesLoading] = useState(false);
  const [permissoes, setPermissoes] = useState<FluxogramaPermissao[]>([]);
  const [usuariosPermissoes, setUsuariosPermissoes] = useState<Usuario[]>([]);
  const [saveConflictOpen, setSaveConflictOpen] = useState(false);
  const [saveBlockedByConflict, setSaveBlockedByConflict] = useState(false);

  const [pessoas, setPessoas] = useState<PanelPessoa[]>([]);
  const [departamentos, setDepartamentos] = useState<PanelDepartamento[]>([]);
  const [projetos, setProjetos] = useState<PanelProjeto[]>([]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const isMobile = useIsMobile();
  const [interactionMode, setInteractionMode] = useState<'select' | 'pan'>('select');
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [canvasHeight, setCanvasHeight] = useState<number | null>(null);
  const canvasAreaRef = useRef<HTMLDivElement | null>(null);
  const canvasResizeRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const handleCanvasResizeStart = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const startHeight = canvasAreaRef.current?.getBoundingClientRect().height ?? 600;
    canvasResizeRef.current = { startY: event.clientY, startHeight };

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!canvasResizeRef.current) return;
      const delta = moveEvent.clientY - canvasResizeRef.current.startY;
      const next = Math.min(1600, Math.max(300, canvasResizeRef.current.startHeight + delta));
      setCanvasHeight(next);
    };
    const onMouseUp = () => {
      canvasResizeRef.current = null;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);
  // Painel de formas: recolhível, com a escolha salva no desktop e fechado no celular.
  const { aberto: paletteOpen, setAberto: setPaletteOpen, alternar: alternarPaleta, celular: telaCelular } =
    usePainelRecolhivel(CHAVE_PALETA_RECOLHIDA);
  const [selectedEdgeIds, setSelectedEdgeIds] = useState<string[]>([]);

  // Guias de alinhamento (helper lines) exibidas durante o arraste de um nó.
  const [helperLineH, setHelperLineH] = useState<number | undefined>(undefined);
  const [helperLineV, setHelperLineV] = useState<number | undefined>(undefined);

  // Menu de contexto (botão direito).
  const [ctxMenu, setCtxMenu] = useState<CtxTarget | null>(null);

  // Ghost preview ao arrastar uma forma da paleta pro canvas.
  const [draggingShape, setDraggingShape] = useState<FluxogramaShape | null>(null);
  const [dragCursor, setDragCursor] = useState<{ x: number; y: number } | null>(null);

  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const { screenToFlowPosition, fitView, getZoom, setCenter } = useReactFlow();
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  // Origem de uma conexão em andamento (para connect-to-create ao soltar no vazio).
  const connectStartRef = useRef<{ nodeId: string; handleId: string | null } | null>(null);

  const { push: pushUndo, undo, redo, canUndo, canRedo, reset: resetUndo, remap: remapUndo } = useUndoRedo<Node, Edge>();

  // Cada edição sobe a revisão: no fim de um salvamento, revisão diferente da enviada = houve edição durante ele.
  const revisaoRef = useRef(0);
  const salvandoRef = useRef(false);
  const [pedidoSalvar, setPedidoSalvar] = useState(0);
  const markDirty = useCallback(() => {
    revisaoRef.current += 1;
    setDirty(true);
  }, []);

  // Captura snapshot do estado atual antes de uma mutação.
  const captureSnapshot = useCallback(() => {
    pushUndo({ nodes, edges });
  }, [edges, nodes, pushUndo]);

  // ============ Carregamento inicial ============
  const carregar = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const flxRes = await fluxogramasService.getById(id);
      if (!flxRes.success) {
        toast.error(flxRes.error || 'Fluxograma não encontrado');
        navigate('/fluxogramas');
        return;
      }

      const loadedFluxograma = flxRes.data;
      const empresaId = loadedFluxograma.empresa_id;
      setFluxograma(loadedFluxograma);
      setSnapEnabled(loadedFluxograma.canvas_config?.snap_to_grid ?? true);

      const [ndsRes, edsRes, deptosRes, orgnNodesRes, projetosRes] = await Promise.all([
        fluxogramaNodesService.listNodes(id),
        fluxogramaNodesService.listEdges(id),
        departamentoService.getPaginated({ page: 1, limit: 200, empresa_id: empresaId }),
        organogramaService.getAll(empresaId),
        projetotService.getAll(undefined, empresaId),
      ]);

      const depts: PanelDepartamento[] = (deptosRes.data || []).map((d) => ({ id: d.id, nome: d.nome }));
      setDepartamentos(depts);
      setProjetos((projetosRes.data || []).map((p) => ({ id: p.id, titulo: p.titulo })));

      const persons: PanelPessoa[] = ((orgnNodesRes.data || []) as Array<{
        id: string;
        tipo: 'departamento' | 'usuario';
        nome_customizado: string | null;
        usuario?: { nome: string };
      }>)
        .filter((n) => n.tipo === 'usuario')
        .map((n) => ({
          id: n.id,
          nome: n.nome_customizado || n.usuario?.nome || 'Sem nome',
        }));
      setPessoas(persons);

      const apiNodes = ndsRes.success ? ndsRes.data : [];
      const apiEdges = edsRes.success ? edsRes.data : [];
      setNodes(orderContainersFirst(apiNodes.map((n) => apiNodeToFlow(n, persons, depts))));
      setEdges(apiEdges.map(apiEdgeToFlow));

      resetUndo();
      setDirty(false);
      setLastSavedAt(null);
      setSaveBlockedByConflict(false);
      setSaveConflictOpen(false);
    } catch (_) {
      toast.error('Erro ao carregar fluxograma');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, resetUndo, setEdges, setNodes]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Rede de segurança: se o drag da paleta terminar sem soltar no canvas
  // (ex.: solto fora da janela, ou cancelado com Esc), limpa o ghost preview.
  useEffect(() => {
    const onDragEnd = () => {
      setDraggingShape(null);
      setDragCursor(null);
    };
    window.addEventListener('dragend', onDragEnd);
    return () => window.removeEventListener('dragend', onDragEnd);
  }, []);

  // Se a empresa ativa mudar e o fluxograma carregado não pertence a ela,
  // volta para a listagem para evitar acesso cruzado entre empresas.
  useEffect(() => {
    if (!fluxograma || !empresaAtiva?.id) return;
    if (user?.tipo === 'superadmin') return;
    if (fluxograma.empresa_id !== empresaAtiva.id) {
      toast.info('Você trocou de empresa. Voltando para a lista de fluxogramas.');
      navigate('/fluxogramas');
    }
  }, [empresaAtiva?.id, fluxograma, navigate, user?.tipo]);

  // ============ Edição inline (label) ============
  const handleLabelChange = useCallback(
    (nodeId: string, newLabel: string) => {
      captureSnapshot();
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== nodeId) return n;
          const d = n.data as ShapeNodeData;
          const apiNode = d.apiNode ? { ...d.apiNode, label: newLabel } : undefined;
          return { ...n, data: { ...d, label: newLabel, apiNode } };
        }),
      );
      markDirty();
    },
    [captureSnapshot, markDirty, setNodes],
  );

  // `handleLabelChange` muda de referência a cada mudança de nodes/edges (via
  // captureSnapshot). Um proxy estável (nunca muda) evita tanto o loop de re-render
  // quanto o closure travado: cada node guarda essa mesma função para sempre, e ela
  // sempre encaminha para a versão mais recente via ref — nenhum node fica "preso"
  // a um snapshot antigo de nodes/edges quando o label é editado.
  const handleLabelChangeRef = useRef(handleLabelChange);
  useEffect(() => {
    handleLabelChangeRef.current = handleLabelChange;
  }, [handleLabelChange]);
  const stableOnLabelChange = useCallback((nodeId: string, newLabel: string) => {
    handleLabelChangeRef.current(nodeId, newLabel);
  }, []);

  // Espelha `selectedNodeIds` numa ref para `handleEditEnd` (abaixo) ler o valor mais
  // recente sem precisar dela nas deps — senão `handleEditEnd` mudaria de referência a
  // cada seleção e o node ficaria "preso" a callbacks antigos entre re-wiring.
  const selectedNodeIdsRef = useRef<string[]>(selectedNodeIds);
  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  // `true` durante um drag-select por retângulo (ver onSelectionStart/onSelectionEnd
  // no <ReactFlow>, abaixo). Usado por `handleSelectionChange` para não confundir a
  // seleção vazia intermediária que o próprio React Flow dispara ao iniciar um
  // drag-select com o clique que confirma uma edição inline.
  const isBoxSelectingRef = useRef(false);

  // Protege a seleção do nó recém-editado inline: o clique que encerra a edição
  // (blur ao clicar fora do texto) pode cair no canvas vazio, e o React Flow trata isso
  // como clique no pane — desmarcando tudo e fechando o painel de propriedades logo após
  // o usuário confirmar o texto. `handleSelectionChange` consulta essa ref para reverter
  // esse deselect específico. O timeout é uma rede de segurança: se nenhuma mudança de
  // seleção consumir a marca (ex.: o clique não chegou a desmarcar nada), ela expira.
  // Só arma a proteção quando o nó editado era a ÚNICA seleção — editar um nó dentro de
  // uma multi-seleção não deve colapsá-la para esse único nó.
  const lastEditedRef = useRef<{ id: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const handleEditEnd = useCallback((nodeId: string) => {
    const sel = selectedNodeIdsRef.current;
    if (sel.length !== 1 || sel[0] !== nodeId) return;
    if (lastEditedRef.current) clearTimeout(lastEditedRef.current.timer);
    const timer = setTimeout(() => {
      lastEditedRef.current = null;
    }, 400);
    lastEditedRef.current = { id: nodeId, timer };
  }, []);

  // Wire callback ao node assim que carregar.
  // `nodes` entra nas deps para recuperar nós recém-carregados do backend, que chegam
  // sem `onLabelChange`/`onEditEnd` (ver `buildNodeData`) — isso inclui não só a carga
  // inicial, mas (até 21/10/2026) TODO save bem-sucedido: `persistSnapshot` reconstruía `nodes` do zero a
  // partir da resposta da API (mesma contagem, novas referências sem callback), então
  // depender só de `nodes.length` deixaria os nós órfãos após o primeiro autosave. O
  // guard `changed` evita loop infinito, já que devolver `prev` inalterado faz o
  // setState fazer bail-out (mesma referência) — o custo extra de rodar em toda
  // mudança de nodes (inclusive drag) é aceitável frente a essa correção.
  useEffect(() => {
    setNodes((prev) => {
      let changed = false;
      const next = prev.map((n) => {
        const d = n.data as ShapeNodeData;
        if (d.onLabelChange === stableOnLabelChange && d.onEditEnd === handleEditEnd && d.readonly === !isAdmin) return n;
        changed = true;
        return { ...n, data: { ...d, onLabelChange: stableOnLabelChange, onEditEnd: handleEditEnd, readonly: !isAdmin } };
      });
      return changed ? next : prev;
    });
  }, [stableOnLabelChange, handleEditEnd, isAdmin, setNodes, nodes]);

  // ============ React Flow changes ============
  const handleNodesChangeWrapped = useCallback(
    (changes: NodeChange[]) => {
      // 'dimensions' só conta quando vem de resize manual (resizing: true).
      // O ResizeObserver do React Flow também dispara 'dimensions' ao remontar um
      // nó (ex.: após save trocar o id temporário pelo id real), sem essa flag —
      // isso marcava o fluxograma como sujo logo depois de salvar.
      const isRealResize = (c: NodeChange) => c.type === 'dimensions' && c.resizing;
      const heavy = changes.some(
        (c) => c.type === 'remove' || c.type === 'add' || c.type === 'replace' || isRealResize(c),
      );
      if (heavy) captureSnapshot();

      // Guias de alinhamento: só quando arrastando exatamente 1 nó.
      let nextChanges = changes;
      const positionChanges = changes.filter(
        (c): c is NodePositionChange => c.type === 'position' && !!c.dragging,
      );
      if (positionChanges.length === 1 && positionChanges[0].position) {
        const helpers = getHelperLines(positionChanges[0], nodes);
        setHelperLineH(helpers.horizontal);
        setHelperLineV(helpers.vertical);
        if (helpers.snapPosition.x !== undefined || helpers.snapPosition.y !== undefined) {
          nextChanges = changes.map((c) => {
            if (c.type !== 'position' || c.id !== positionChanges[0].id || !c.position) return c;
            return {
              ...c,
              position: {
                x: helpers.snapPosition.x ?? c.position.x,
                y: helpers.snapPosition.y ?? c.position.y,
              },
            };
          });
        }
      } else if (helperLineH !== undefined || helperLineV !== undefined) {
        setHelperLineH(undefined);
        setHelperLineV(undefined);
      }

      onNodesChange(nextChanges);
      const meaningful = changes.some(
        (c) => c.type === 'position' || c.type === 'add' || c.type === 'remove' || c.type === 'replace' || isRealResize(c),
      );
      if (meaningful) markDirty();
    },
    [captureSnapshot, helperLineH, helperLineV, markDirty, nodes, onNodesChange],
  );

  const handleEdgesChangeWrapped = useCallback(
    (changes: EdgeChange[]) => {
      const heavy = changes.some((c) => c.type === 'remove' || c.type === 'add' || c.type === 'replace');
      if (heavy) captureSnapshot();
      onEdgesChange(changes);
      const meaningful = changes.some((c) => c.type === 'add' || c.type === 'remove' || c.type === 'replace');
      if (meaningful) markDirty();
    },
    [captureSnapshot, markDirty, onEdgesChange],
  );

  const handleConnect = useCallback(
    (params: Connection) => {
      captureSnapshot();
      setEdges((prev) =>
        addEdge(
          {
            ...params,
            id: cuid(),
            type: 'default',
            markerEnd: arrowMarker(null),
            data: { apiEdge: undefined },
          },
          prev,
        ),
      );
      markDirty();
    },
    [captureSnapshot, markDirty, setEdges],
  );

  // ============ Ligar arrastando um item sobre o outro ============
  // Segurar um item ~0,5 s em cima de outro mostra "Solte para ligar com seta"; soltar cria a seta
  // (do item de baixo para o arrastado) e devolve o arrastado à posição de antes. Tirar de cima cancela.
  // Geometria em lib/fluxogramaLigacao.ts. Os retângulos dos outros itens são calculados uma vez no início
  // do arraste (eles não se mexem enquanto isso), então cada movimento só testa o centro do arrastado.
  const ligacaoRef = useRef<{
    arrastadoId: string;
    inicio: { x: number; y: number };
    posicaoPai: { x: number; y: number } | null;
    candidatos: Retangulo[];
    alvoId: string | null;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  const [ligacaoAlvo, setLigacaoAlvo] = useState<{ arrastadoId: string; alvoId: string; seloEmBaixo: boolean } | null>(null);

  const cancelarLigacao = useCallback(() => {
    if (ligacaoRef.current?.timer) clearTimeout(ligacaoRef.current.timer);
    ligacaoRef.current = null;
    setLigacaoAlvo(null);
  }, []);

  // Editor desmontado no meio de um arraste: não deixa o timer disparar depois.
  useEffect(
    () => () => {
      if (ligacaoRef.current?.timer) clearTimeout(ligacaoRef.current.timer);
      ligacaoRef.current = null;
    },
    [],
  );

  // Drag de nó: snapshot só uma vez no início do drag.
  const handleNodeDragStart = useCallback(
    (_evt: unknown, node: Node, arrastados: Node[]) => {
      captureSnapshot();
      cancelarLigacao();
      // Só com um item (não container) por vez: arrastar uma seleção inteira continua só movendo.
      if (!isAdmin || arrastados.length > 1 || isContainerShape((node.data as ShapeNodeData)?.shape)) return;
      const ehContainer = new Set(
        nodes.filter((n) => isContainerShape((n.data as ShapeNodeData)?.shape)).map((n) => n.id),
      );
      const pai = node.parentId ? nodes.find((n) => n.id === node.parentId) : undefined;
      ligacaoRef.current = {
        arrastadoId: node.id,
        inicio: { ...node.position },
        posicaoPai: pai ? { ...pai.position } : null,
        candidatos: retangulosAbsolutos(nodes).filter((r) => r.id !== node.id && !ehContainer.has(r.id)),
        alvoId: null,
        timer: null,
      };
    },
    [captureSnapshot, cancelarLigacao, isAdmin, nodes],
  );

  const handleNodeDrag = useCallback(
    (_evt: unknown, node: Node) => {
      const lig = ligacaoRef.current;
      if (!lig || lig.arrastadoId !== node.id) return;
      const alvoId = alvoNoPonto(lig.candidatos, centroAbsoluto(node, lig.posicaoPai));
      if (alvoId === lig.alvoId) return;
      // Mudou de alvo (ou saiu de cima): recomeça a contagem e esconde o aviso.
      if (lig.timer) clearTimeout(lig.timer);
      lig.timer = null;
      lig.alvoId = alvoId;
      setLigacaoAlvo(null);
      if (alvoId) {
        lig.timer = setTimeout(() => {
          if (ligacaoRef.current !== lig || lig.alvoId !== alvoId) return;
          // Selo acima do item; se não couber (item no topo da área visível), vai para baixo.
          const area = reactFlowWrapper.current;
          const el = area?.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(alvoId)}"]`);
          const seloEmBaixo =
            !!area && !!el && el.getBoundingClientRect().top - area.getBoundingClientRect().top < 48;
          setLigacaoAlvo({ arrastadoId: lig.arrastadoId, alvoId, seloEmBaixo });
        }, LIGAR_SEGURANDO_MS);
      }
    },
    [],
  );

  // Ao terminar o arraste: some com as guias e resolve entrada/saída de containers.
  const handleNodeDragStop = useCallback(
    (_evt: unknown, dragged: Node) => {
      setHelperLineH(undefined);
      setHelperLineV(undefined);
      const lig = ligacaoRef.current;
      const alvoPronto = ligacaoAlvo;
      cancelarLigacao();
      if (!isAdmin) return;

      // Soltou com o aviso "Solte para ligar" visível e ainda em cima do mesmo item: liga e devolve o
      // arrastado à posição de antes (o snapshot do início do arraste permite desfazer tudo de uma vez).
      if (
        lig &&
        alvoPronto &&
        alvoPronto.arrastadoId === dragged.id &&
        alvoNoPonto(lig.candidatos, centroAbsoluto(dragged, lig.posicaoPai)) === alvoPronto.alvoId
      ) {
        // Lados pela posição: o arrastado volta ao lugar original, então a seta liga as faces que se olham.
        const alvoRet = lig.candidatos.find((r) => r.id === alvoPronto.alvoId);
        const lados = alvoRet
          ? ladosDaSeta(
              { x: alvoRet.x + alvoRet.w / 2, y: alvoRet.y + alvoRet.h / 2 },
              centroAbsoluto({ ...dragged, position: lig.inicio }, lig.posicaoPai),
            )
          : { sourceHandle: null, targetHandle: null };
        setNodes((prev) => prev.map((n) => (n.id === dragged.id ? { ...n, position: lig.inicio } : n)));
        setEdges((prev) =>
          prev.some((e) => e.source === alvoPronto.alvoId && e.target === dragged.id)
            ? prev
            : addEdge(
                {
                  source: alvoPronto.alvoId,
                  target: dragged.id,
                  sourceHandle: lados.sourceHandle,
                  targetHandle: lados.targetHandle,
                  id: cuid(),
                  type: 'default',
                  markerEnd: arrowMarker(null),
                  data: { apiEdge: undefined },
                },
                prev,
              ),
        );
        markDirty();
        return;
      }

      const draggedShape = (dragged.data as ShapeNodeData)?.shape;
      if (isContainerShape(draggedShape)) return; // containers/raias não entram em outros

      const dimOf = (n: Node) => ({
        w: typeof n.width === 'number' ? n.width : (n.data as ShapeNodeData).width,
        h: typeof n.height === 'number' ? n.height : (n.data as ShapeNodeData).height,
      });
      const containers = nodes.filter((n) => isContainerShape((n.data as ShapeNodeData)?.shape));
      const parentById = new Map(nodes.map((n) => [n.id, n] as const));

      // Posição absoluta do nó arrastado (usa a posição fresca do parâmetro).
      const oldParent = dragged.parentId ? parentById.get(dragged.parentId) : undefined;
      const absX = (oldParent ? oldParent.position.x : 0) + dragged.position.x;
      const absY = (oldParent ? oldParent.position.y : 0) + dragged.position.y;
      const { w, h } = dimOf(dragged);
      const cx = absX + w / 2;
      const cy = absY + h / 2;

      // Container cujo centro do nó cai dentro (menor área, se sobrepostos).
      let target: Node | null = null;
      let targetArea = Infinity;
      for (const c of containers) {
        if (c.id === dragged.id) continue;
        const { w: cw, h: ch } = dimOf(c);
        if (cx >= c.position.x && cx <= c.position.x + cw && cy >= c.position.y && cy <= c.position.y + ch) {
          const area = cw * ch;
          if (area < targetArea) {
            target = c;
            targetArea = area;
          }
        }
      }

      const currentParentId = dragged.parentId ?? null;
      const nextParentId = target ? target.id : null;
      if (currentParentId === nextParentId) return; // nada muda

      captureSnapshot();
      setNodes((prev) => {
        const mapped = prev.map((n) => {
          if (n.id !== dragged.id) return n;
          if (target) {
            // Entra no container: posição relativa ao pai.
            return { ...n, parentId: target.id, position: { x: absX - target.position.x, y: absY - target.position.y } };
          }
          // Sai do container: volta para posição absoluta.
          const rest = { ...n, position: { x: absX, y: absY } };
          delete (rest as { parentId?: string }).parentId;
          return rest;
        });
        return orderContainersFirst(mapped);
      });
      markDirty();
    },
    [cancelarLigacao, captureSnapshot, isAdmin, ligacaoAlvo, markDirty, nodes, setEdges, setNodes],
  );

  // ============ Connect-to-create ============
  // Guarda a origem quando o usuário começa a puxar uma conexão.
  const handleConnectStart = useCallback(
    (_: unknown, params: { nodeId: string | null; handleId: string | null }) => {
      if (params.nodeId) {
        connectStartRef.current = { nodeId: params.nodeId, handleId: params.handleId };
      }
    },
    [],
  );

  // Se soltar no vazio, cria um nó "process" já conectado à origem.
  const handleConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent, connectionState: FinalConnectionState) => {
      const origin = connectStartRef.current;
      connectStartRef.current = null;
      if (!isAdmin) return;
      // Só cria nó se a conexão NÃO terminou num handle válido.
      if (connectionState.isValid || !origin) return;

      const point = 'changedTouches' in event ? event.changedTouches[0] : event;
      const position = screenToFlowPosition({ x: point.clientX, y: point.clientY });
      const meta = SHAPE_META.process;
      const newId = cuid();

      captureSnapshot();
      const newNode: Node = {
        id: newId,
        type: 'shape',
        position: { x: position.x - meta.defaultWidth / 2, y: position.y - meta.defaultHeight / 2 },
        data: {
          label: meta.label,
          shape: 'process',
          width: meta.defaultWidth,
          height: meta.defaultHeight,
          style: null,
          descricao: null,
          responsavelLabel: null,
          departamentoLabel: null,
          apiNode: undefined,
          onLabelChange: handleLabelChange,
          onEditEnd: handleEditEnd,
          readonly: !isAdmin,
        } as ShapeNodeData,
        width: meta.defaultWidth,
        height: meta.defaultHeight,
        style: { width: meta.defaultWidth, height: meta.defaultHeight },
        selected: true,
      };

      setNodes((prev) => [...prev.map((n) => ({ ...n, selected: false })), newNode]);
      setEdges((prev) =>
        addEdge(
          {
            id: cuid(),
            source: origin.nodeId,
            sourceHandle: origin.handleId ?? undefined,
            target: newId,
            targetHandle: `t-left`,
            type: 'default',
            markerEnd: arrowMarker(null),
            data: { apiEdge: undefined },
          },
          prev,
        ),
      );
      setSelectedNodeIds([newId]);
      markDirty();
    },
    [captureSnapshot, handleEditEnd, handleLabelChange, isAdmin, markDirty, screenToFlowPosition, setEdges, setNodes],
  );

  // ============ Drop a partir da paleta ============
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragCursor({ x: e.clientX, y: e.clientY });
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Só limpa se realmente saiu do wrapper (não de um filho interno).
    if (e.currentTarget.contains(e.relatedTarget as globalThis.Node | null)) return;
    setDragCursor(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!isAdmin) return;
      const shape = e.dataTransfer.getData('application/x-fluxograma-shape') as FluxogramaShape;
      if (!shape || !SHAPE_META[shape]) return;
      captureSnapshot();
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const meta = SHAPE_META[shape];
      const newId = cuid();

      // Se o ponto do drop cai dentro de um container/raia (e a forma não é ela
      // mesma um container), já nasce como filho — sem precisar arrastar depois.
      let parentId: string | undefined;
      let topLeft = { x: position.x - meta.defaultWidth / 2, y: position.y - meta.defaultHeight / 2 };
      if (!isContainerShape(shape)) {
        const containers = nodes.filter((n) => isContainerShape((n.data as ShapeNodeData)?.shape));
        let target: Node | null = null;
        let targetArea = Infinity;
        for (const c of containers) {
          const cw = typeof c.width === 'number' ? c.width : (c.data as ShapeNodeData).width;
          const ch = typeof c.height === 'number' ? c.height : (c.data as ShapeNodeData).height;
          if (
            position.x >= c.position.x &&
            position.x <= c.position.x + cw &&
            position.y >= c.position.y &&
            position.y <= c.position.y + ch
          ) {
            const area = cw * ch;
            if (area < targetArea) {
              target = c;
              targetArea = area;
            }
          }
        }
        if (target) {
          parentId = target.id;
          topLeft = { x: topLeft.x - target.position.x, y: topLeft.y - target.position.y };
        }
      }

      const newNode: Node = {
        id: newId,
        type: 'shape',
        position: topLeft,
        ...(parentId ? { parentId } : {}),
        data: {
          label: meta.label,
          shape,
          width: meta.defaultWidth,
          height: meta.defaultHeight,
          style: null,
          descricao: null,
          responsavelLabel: null,
          departamentoLabel: null,
          apiNode: undefined,
          onLabelChange: handleLabelChange,
          onEditEnd: handleEditEnd,
          readonly: !isAdmin,
        } as ShapeNodeData,
        width: meta.defaultWidth,
        height: meta.defaultHeight,
        style: { width: meta.defaultWidth, height: meta.defaultHeight },
        selected: true,
      };

      setNodes((prev) => orderContainersFirst([...prev.map((n) => ({ ...n, selected: false })), newNode]));
      setSelectedNodeIds([newId]);
      markDirty();
      setDraggingShape(null);
      setDragCursor(null);
    },
    [captureSnapshot, handleEditEnd, handleLabelChange, isAdmin, markDirty, nodes, screenToFlowPosition, setNodes],
  );

  // ============ Selection ============
  const handleSelectionChange = useCallback(
    ({ nodes: sel, edges: selEdges }: { nodes: Node[]; edges: Edge[] }) => {
      const pending = lastEditedRef.current;
      const selIds = sel.map((n) => n.id);

      // Clique que confirmou uma edição inline caiu no canvas vazio: em vez de
      // desmarcar tudo, mantém o nó recém-editado selecionado (ver `handleEditEnd`).
      // Ignorado durante um drag-select (`isBoxSelectingRef`) — o React Flow dispara essa
      // mesma seleção vazia como evento intermediário ao iniciar o retângulo de seleção,
      // e o nó pode já ter sido excluído entretanto (daí a checagem em `nodes`).
      if (
        sel.length === 0 &&
        selEdges.length === 0 &&
        pending &&
        !isBoxSelectingRef.current &&
        nodes.some((n) => n.id === pending.id)
      ) {
        clearTimeout(pending.timer);
        lastEditedRef.current = null;
        setSelectedNodeIds([pending.id]);
        setSelectedEdgeIds([]);
        setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === pending.id })));
        return;
      }

      // Eco: `onLabelChange` reescreve `data` do nó editado, e o React Flow reemite a
      // seleção (ainda com esse mesmo nó, sozinho) antes do clique real que a encerra
      // chegar. Não é uma mudança de seleção de verdade — aplica normalmente mas mantém
      // a proteção armada para o próximo evento (o deselect de fato).
      if (pending && selEdges.length === 0 && selIds.length === 1 && selIds[0] === pending.id) {
        setSelectedNodeIds(selIds);
        setSelectedEdgeIds([]);
        return;
      }

      if (pending) {
        clearTimeout(pending.timer);
        lastEditedRef.current = null;
      }
      setSelectedNodeIds(selIds);
      setSelectedEdgeIds(selEdges.map((e) => e.id));
    },
    [nodes, setNodes],
  );

  const selectedNode = useMemo(
    () =>
      selectedNodeIds.length === 1 ? nodes.find((n) => n.id === selectedNodeIds[0]) || null : null,
    [nodes, selectedNodeIds],
  );

  /**
   * Sintetiza um ApiNode completo a partir do estado do nó React Flow + ShapeNodeData.
   * Usado tanto para popular o painel quanto para garantir que `nextApi` em
   * `handlePanelChange` sempre tenha todos os campos (evita TypeError quando o
   * nó ainda é local, sem `apiNode` persistido).
   */
  const synthesizeApiNode = useCallback(
    (n: Node, d: ShapeNodeData): ApiNode => {
      const fromApi = d.apiNode || {};
      const base: ApiNode = {
        id: n.id,
        empresa_id: fluxograma?.empresa_id || '',
        fluxograma_id: fluxograma?.id || '',
        shape: d.shape,
        label: d.label,
        descricao: d.descricao ?? null,
        position_x: n.position.x,
        position_y: n.position.y,
        width: d.width,
        height: d.height,
        style: d.style ?? null,
        responsavel_node_id: null,
        departamento_id: null,
        z_index: 0,
      };
      // Estado do `data` (ao vivo) tem prioridade sobre `apiNode` (snapshot prévio)
      return {
        ...base,
        ...fromApi,
        shape: d.shape,
        label: d.label,
        descricao: d.descricao ?? (fromApi as Partial<ApiNode>).descricao ?? null,
        style: d.style ?? null,
      };
    },
    [fluxograma],
  );

  const selectedApiNode: ApiNode | null = useMemo(() => {
    if (!selectedNode) return null;
    const d = selectedNode.data as ShapeNodeData;
    return synthesizeApiNode(selectedNode, d);
  }, [selectedNode, synthesizeApiNode]);

  const handlePanelChange = useCallback(
    (updates: Partial<ApiNode>) => {
      if (selectedNodeIds.length !== 1) return;
      const onlyId = selectedNodeIds[0];
      captureSnapshot();
      setNodes((prev) =>
        prev.map((n) => {
          if (n.id !== onlyId) return n;
          const d = n.data as ShapeNodeData;
          // Garante shape/label/posição/etc. mesmo se d.apiNode ainda for indefinido
          const baseApi = synthesizeApiNode(n, d);
          const nextApi: ApiNode = { ...baseApi, ...updates };
          const nextLabel = updates.label ?? d.label;
          const nextStyle = updates.style === undefined ? d.style : updates.style;
          const nextDescricao =
            updates.descricao === undefined ? d.descricao ?? null : updates.descricao;
          return {
            ...n,
            data: {
              ...d,
              label: nextLabel,
              style: nextStyle,
              descricao: nextDescricao,
              apiNode: nextApi,
              responsavelLabel: nextApi.responsavel_node_id
                ? pessoas.find((p) => p.id === nextApi.responsavel_node_id)?.nome ?? null
                : null,
              departamentoLabel: nextApi.departamento_id
                ? departamentos.find((dep) => dep.id === nextApi.departamento_id)?.nome ?? null
                : null,
            },
          };
        }),
      );
      markDirty();
    },
    [captureSnapshot, departamentos, markDirty, pessoas, selectedNodeIds, setNodes, synthesizeApiNode],
  );

  // ============ Edição de aresta ============
  const synthesizeApiEdge = useCallback(
    (e: Edge): ApiEdge => {
      const prev = (e.data as { apiEdge?: ApiEdge })?.apiEdge;
      const rawTarget =
        typeof e.targetHandle === 'string'
          ? e.targetHandle.startsWith('t-')
            ? e.targetHandle.slice(2)
            : e.targetHandle
          : null;
      return {
        id: e.id,
        empresa_id: fluxograma?.empresa_id || '',
        fluxograma_id: fluxograma?.id || '',
        source_node_id: e.source,
        target_node_id: e.target,
        source_handle: (e.sourceHandle as ApiEdge['source_handle']) ?? null,
        target_handle: (rawTarget as ApiEdge['target_handle']) ?? null,
        tipo: prev?.tipo ?? 'solid',
        style_curva: prev?.style_curva ?? 'bezier',
        label: typeof e.label === 'string' ? e.label : prev?.label ?? null,
        arrow_start: prev?.arrow_start ?? false,
        arrow_end: prev?.arrow_end ?? true,
        color: prev?.color ?? null,
      };
    },
    [fluxograma],
  );

  const selectedApiEdge: ApiEdge | null = useMemo(() => {
    if (selectedEdgeIds.length !== 1) return null;
    const e = edges.find((ed) => ed.id === selectedEdgeIds[0]);
    return e ? synthesizeApiEdge(e) : null;
  }, [edges, selectedEdgeIds, synthesizeApiEdge]);

  const handleEdgePanelChange = useCallback(
    (updates: Partial<ApiEdge>) => {
      if (selectedEdgeIds.length !== 1 || !isAdmin) return;
      const onlyId = selectedEdgeIds[0];
      captureSnapshot();
      setEdges((prev) =>
        prev.map((e) => {
          if (e.id !== onlyId) return e;
          const nextApi: ApiEdge = { ...synthesizeApiEdge(e), ...updates };
          return {
            ...e,
            type:
              nextApi.style_curva === 'step'
                ? 'step'
                : nextApi.style_curva === 'straight'
                  ? 'straight'
                  : 'default',
            label: nextApi.label ?? undefined,
            style: {
              ...e.style,
              strokeDasharray:
                nextApi.tipo === 'dashed' ? '6 4' : nextApi.tipo === 'dotted' ? '2 4' : undefined,
              stroke: nextApi.color ?? undefined,
            },
            markerEnd: nextApi.arrow_end ? arrowMarker(nextApi.color) : undefined,
            markerStart: nextApi.arrow_start ? arrowMarker(nextApi.color) : undefined,
            data: { apiEdge: nextApi },
          };
        }),
      );
      markDirty();
    },
    [captureSnapshot, isAdmin, markDirty, selectedEdgeIds, setEdges, synthesizeApiEdge],
  );

  const handleReverseEdge = useCallback(
    (edgeId?: string) => {
    const onlyId = edgeId ?? selectedEdgeIds[0];
    if (!onlyId || !isAdmin) return;
    captureSnapshot();
    setEdges((prev) =>
      prev.map((e) => {
        if (e.id !== onlyId) return e;
        const newSourceHandle =
          typeof e.targetHandle === 'string' && e.targetHandle.startsWith('t-')
            ? e.targetHandle.slice(2)
            : (e.targetHandle ?? undefined);
        const newTargetHandle = e.sourceHandle ? `t-${e.sourceHandle}` : undefined;
        const api = synthesizeApiEdge(e);
        const swappedApi: ApiEdge = {
          ...api,
          source_node_id: e.target,
          target_node_id: e.source,
          source_handle: api.target_handle,
          target_handle: api.source_handle,
        };
        return {
          ...e,
          source: e.target,
          target: e.source,
          sourceHandle: newSourceHandle,
          targetHandle: newTargetHandle,
          data: { apiEdge: swappedApi },
        };
      }),
    );
    markDirty();
  }, [captureSnapshot, isAdmin, markDirty, selectedEdgeIds, setEdges, synthesizeApiEdge]);

  // ============ Snapshot (autosave) ============
  const buildSnapshotPayload = useCallback(() => {
    if (!id) return null;
    const snapshotNodes: SnapshotNode[] = nodes.map((n) => {
      const d = n.data as ShapeNodeData;
      const apiNode = d.apiNode;
      const w = typeof n.width === 'number' ? n.width : d.width;
      const h = typeof n.height === 'number' ? n.height : d.height;
      return {
        client_id: n.id,
        id: apiNode?.id ?? null,
        shape: d.shape,
        label: d.label,
        // `d.descricao` é a fonte viva (atualizada pelo painel); cai em apiNode como backup
        descricao: d.descricao !== undefined ? d.descricao : apiNode?.descricao ?? null,
        position_x: n.position.x,
        position_y: n.position.y,
        width: w,
        height: h,
        style: d.style ?? null,
        responsavel_node_id: apiNode?.responsavel_node_id ?? null,
        departamento_id: apiNode?.departamento_id ?? null,
        z_index: typeof n.zIndex === 'number' ? n.zIndex : apiNode?.z_index ?? 0,
        parent_client_id: n.parentId ?? null,
      };
    });

    const snapshotEdges: SnapshotEdge[] = edges.map((e) => {
      const apiEdge = (e.data as { apiEdge?: ApiEdge })?.apiEdge;
      const target_handle = e.targetHandle
        ? e.targetHandle.startsWith('t-')
          ? (e.targetHandle.slice(2) as SnapshotEdge['target_handle'])
          : (e.targetHandle as SnapshotEdge['target_handle'])
        : null;
      return {
        client_id: e.id,
        id: apiEdge?.id ?? null,
        source_client_id: e.source,
        target_client_id: e.target,
        source_handle: (e.sourceHandle as SnapshotEdge['source_handle']) ?? null,
        target_handle,
        tipo: apiEdge?.tipo ?? 'solid',
        style_curva: apiEdge?.style_curva ?? 'bezier',
        label: typeof e.label === 'string' ? e.label : apiEdge?.label ?? null,
        arrow_start: apiEdge?.arrow_start ?? false,
        arrow_end: apiEdge?.arrow_end ?? true,
        color: apiEdge?.color ?? null,
      };
    });

    return {
      fluxograma_id: id,
      updatedAt: fluxograma?.updatedAt ?? null,
      nodes: snapshotNodes,
      edges: snapshotEdges,
    };
  }, [edges, id, nodes]);

  const persistSnapshot = useCallback(async () => {
    // Um salvamento por vez: dois em paralelo mandariam o mesmo `updatedAt` e o segundo daria conflito.
    // O que for editado durante este é salvo em seguida (ver `pedidoSalvar` no fim).
    if (salvandoRef.current) return;
    const payload = buildSnapshotPayload();
    if (!payload) return;
    if (saveBlockedByConflict) return;
    salvandoRef.current = true;
    const revisaoEnviada = revisaoRef.current;
    setSaving(true);
    try {
      const result = await fluxogramaNodesService.saveSnapshot(payload);
      if (result.success) {
        // Não troca o estado pela resposta (isso apagava o que foi editado durante o salvamento e a seleção):
        // só aplica os ids que o banco deu às formas/setas novas, no estado, na seleção e no histórico.
        const resposta: RespostaSalvamento = {
          ids: { nos: result.data.id_map?.nodes ?? {}, setas: result.data.id_map?.edges ?? {} },
          nos: new Map(result.data.nodes.map((n) => [n.id, n] as const)),
          setas: new Map(result.data.edges.map((e) => [e.id, e] as const)),
        };
        const novoIdNo = (idNo: string) => resposta.ids.nos[idNo] ?? idNo;
        setNodes((prev) => aplicarIdsNosNos(prev, resposta));
        setEdges((prev) => aplicarIdsNasSetas(prev, resposta));
        setSelectedNodeIds((prev) => prev.map(novoIdNo));
        setSelectedEdgeIds((prev) => prev.map((idSeta) => resposta.ids.setas[idSeta] ?? idSeta));
        if (lastEditedRef.current) lastEditedRef.current.id = novoIdNo(lastEditedRef.current.id);
        remapUndo((snap) => ({
          nodes: aplicarIdsNosNos(snap.nodes, resposta),
          edges: aplicarIdsNasSetas(snap.edges, resposta),
        }));
        setFluxograma((prev) =>
          prev
            ? {
                ...prev,
                updatedAt: result.data.updatedAt || new Date().toISOString(),
              }
            : prev,
        );
        setLastSavedAt(new Date());
        if (revisaoRef.current === revisaoEnviada) setDirty(false);
        else setPedidoSalvar((n) => n + 1); // houve edição durante o salvamento: salva de novo
      } else {
        console.error('[fluxograma] saveSnapshot retornou erro:', result.error, payload);
        if (result.error?.includes('desatualizado')) {
          if (autosaveTimer.current) {
            clearTimeout(autosaveTimer.current);
            autosaveTimer.current = null;
          }
          setSaveBlockedByConflict(true);
          setSaveConflictOpen(true);
          return;
        }
        toast.error(result.error || 'Erro ao salvar');
      }
    } catch (err) {
      console.error('[fluxograma] saveSnapshot exception:', err, payload);
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      if (msg.includes('desatualizado')) {
        if (autosaveTimer.current) {
          clearTimeout(autosaveTimer.current);
          autosaveTimer.current = null;
        }
        setSaveBlockedByConflict(true);
        setSaveConflictOpen(true);
        return;
      }
      toast.error(msg);
    } finally {
      salvandoRef.current = false;
      setSaving(false);
    }
  }, [buildSnapshotPayload, remapUndo, saveBlockedByConflict, setEdges, setNodes]);

  const handleSaveNow = useCallback(async () => {
    if (autosaveTimer.current) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
    await persistSnapshot();
  }, [persistSnapshot]);

  useEffect(() => {
    if (!dirty) return;
    if (saveBlockedByConflict) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      persistSnapshot();
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [dirty, pedidoSalvar, persistSnapshot, saveBlockedByConflict]);

  const commitRename = useCallback(async () => {
    setIsRenamingTitle(false);
    const trimmed = renameValue.trim();
    if (!fluxograma || !trimmed || trimmed === fluxograma.nome) return;

    const result = await fluxogramasService.update(fluxograma.id, { nome: trimmed });
    if (!result.success || !result.data) {
      toast.error(result.error || 'Erro ao renomear fluxograma');
      return;
    }
    setFluxograma(result.data);
    toast.success('Fluxograma renomeado');
  }, [fluxograma, renameValue]);

  const handleReloadAfterConflict = useCallback(async () => {
    setSaveConflictOpen(false);
    await carregar();
    toast.success('Fluxograma recarregado com a versão mais recente');
  }, [carregar]);

  // ============ Undo / Redo handlers ============
  const handleUndo = useCallback(() => {
    const prev = undo({ nodes, edges });
    if (!prev) return;
    setNodes(prev.nodes);
    setEdges(prev.edges);
    markDirty();
  }, [edges, markDirty, nodes, setEdges, setNodes, undo]);

  const handleRedo = useCallback(() => {
    const next = redo({ nodes, edges });
    if (!next) return;
    setNodes(next.nodes);
    setEdges(next.edges);
    markDirty();
  }, [edges, markDirty, nodes, redo, setEdges, setNodes]);

  // ============ Duplicate / Delete / Align / Auto-layout ============
  const handleDuplicateSelection = useCallback(() => {
    if (selectedNodeIds.length === 0 || !isAdmin) return;
    captureSnapshot();
    const dx = 40;
    const dy = 40;
    setNodes((prev) => {
      const selected = prev.filter((n) => selectedNodeIds.includes(n.id));
      // Mapa id-original -> id-clone, para remapear parentId quando o container
      // também estiver sendo duplicado junto com seus filhos.
      const idMap = new Map(selected.map((n) => [n.id, cuid()] as const));
      const clones: Node[] = selected.map((n) => {
        const d = n.data as ShapeNodeData;
        const parentAlsoCloned = n.parentId ? idMap.get(n.parentId) : undefined;
        return {
          ...n,
          id: idMap.get(n.id)!,
          // Se o pai também foi clonado, a posição relativa não muda (o pai já
          // se move dx/dy); senão, desloca normalmente.
          position: parentAlsoCloned
            ? { x: n.position.x, y: n.position.y }
            : { x: n.position.x + dx, y: n.position.y + dy },
          ...(parentAlsoCloned ? { parentId: parentAlsoCloned } : {}),
          selected: true,
          data: {
            ...d,
            apiNode: undefined, // copia local até ser salva
            onLabelChange: handleLabelChange,
            onEditEnd: handleEditEnd,
            readonly: !isAdmin,
          },
        };
      });
      return orderContainersFirst([...prev.map((n) => ({ ...n, selected: false })), ...clones]);
    });
    markDirty();
  }, [captureSnapshot, handleEditEnd, handleLabelChange, isAdmin, markDirty, selectedNodeIds, setNodes]);

  const handleDeleteSelection = useCallback(() => {
    if (selectedNodeIds.length === 0 || !isAdmin) return;
    captureSnapshot();
    setNodes((prev) => prev.filter((n) => !selectedNodeIds.includes(n.id)));
    setEdges((prev) =>
      prev.filter((e) => !selectedNodeIds.includes(e.source) && !selectedNodeIds.includes(e.target)),
    );
    setSelectedNodeIds([]);
    markDirty();
  }, [captureSnapshot, isAdmin, markDirty, selectedNodeIds, setEdges, setNodes]);

  // Cor de preenchimento rápida (mini-toolbar), aplicável a múltiplos nós.
  const handleQuickColor = useCallback(
    (color: string) => {
      if (selectedNodeIds.length === 0 || !isAdmin) return;
      captureSnapshot();
      setNodes((prev) =>
        prev.map((n) => {
          if (!selectedNodeIds.includes(n.id)) return n;
          const d = n.data as ShapeNodeData;
          const style = { ...(d.style || {}), bg_color: color };
          const apiNode = d.apiNode ? { ...d.apiNode, style } : d.apiNode;
          return { ...n, data: { ...d, style, apiNode } };
        }),
      );
      markDirty();
    },
    [captureSnapshot, isAdmin, markDirty, selectedNodeIds, setNodes],
  );

  // Trazer à frente / enviar para trás (z_index).
  const handleReorder = useCallback(
    (dir: 'front' | 'back') => {
      if (selectedNodeIds.length === 0 || !isAdmin) return;
      captureSnapshot();
      setNodes((prev) => {
        const zs = prev.map((n) => (typeof n.zIndex === 'number' ? n.zIndex : 0));
        const targetZ = dir === 'front' ? Math.max(0, ...zs) + 1 : Math.min(0, ...zs) - 1;
        return prev.map((n) => {
          if (!selectedNodeIds.includes(n.id)) return n;
          const d = n.data as ShapeNodeData;
          const apiNode = d.apiNode ? { ...d.apiNode, z_index: targetZ } : d.apiNode;
          return { ...n, zIndex: targetZ, data: { ...d, apiNode } };
        });
      });
      markDirty();
    },
    [captureSnapshot, isAdmin, markDirty, selectedNodeIds, setNodes],
  );

  const handleDeleteEdgeById = useCallback(
    (edgeId: string) => {
      if (!isAdmin) return;
      captureSnapshot();
      setEdges((prev) => prev.filter((e) => e.id !== edgeId));
      setSelectedEdgeIds([]);
      markDirty();
    },
    [captureSnapshot, isAdmin, markDirty, setEdges],
  );

  // ============ Copiar / Colar / Recortar ============
  const copySelection = useCallback(() => {
    if (selectedNodeIds.length === 0) return;
    const sel = new Set(selectedNodeIds);
    const selNodes = nodes.filter((n) => sel.has(n.id));
    if (selNodes.length === 0) return;
    const byId = new Map(nodes.map((n) => [n.id, n] as const));
    const clipNodes: ClipboardNode[] = selNodes.map((n) => {
      const d = n.data as ShapeNodeData;
      // Se o pai (container/raia) também está selecionado, preserva a relação
      // e mantém a posição relativa; senão, "solta" o nó com sua posição absoluta.
      const parentInSelection = n.parentId && sel.has(n.parentId);
      const pos = parentInSelection ? n.position : absolutePositionOf(n, byId);
      return {
        client_id: n.id,
        shape: d.shape,
        label: d.label,
        descricao: d.descricao ?? null,
        width: typeof n.width === 'number' ? n.width : d.width,
        height: typeof n.height === 'number' ? n.height : d.height,
        style: d.style ?? null,
        position_x: pos.x,
        position_y: pos.y,
        parent_client_id: parentInSelection ? (n.parentId as string) : null,
      };
    });
    const clipEdges: ClipboardEdge[] = edges
      .filter((e) => sel.has(e.source) && sel.has(e.target))
      .map((e) => ({
        source: e.source,
        target: e.target,
        sourceHandle: (e.sourceHandle as string | undefined) ?? null,
        targetHandle: (e.targetHandle as string | undefined) ?? null,
        apiEdge: (e.data as { apiEdge?: ApiEdge })?.apiEdge ?? null,
      }));
    try {
      const payload: ClipboardPayload = { nodes: clipNodes, edges: clipEdges };
      localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload));
      toast.success(`${clipNodes.length} ${clipNodes.length === 1 ? 'item copiado' : 'itens copiados'}`);
    } catch {
      /* cota de storage excedida — ignora */
    }
  }, [edges, nodes, selectedNodeIds]);

  const pasteClipboard = useCallback(() => {
    if (!isAdmin) return;
    let parsed: ClipboardPayload | null = null;
    try {
      parsed = JSON.parse(localStorage.getItem(CLIPBOARD_KEY) || 'null') as ClipboardPayload | null;
    } catch {
      return;
    }
    if (!parsed?.nodes?.length) return;
    captureSnapshot();
    const OFFSET = 32;
    const idMap = new Map<string, string>();
    const newNodes: Node[] = parsed.nodes.map((cn) => {
      const newId = cuid();
      idMap.set(cn.client_id, newId);
      const meta = SHAPE_META[cn.shape] ?? SHAPE_META.process;
      const w = cn.width ?? meta.defaultWidth;
      const h = cn.height ?? meta.defaultHeight;
      // Se o pai também está sendo colado (client_id presente no idMap), mantém
      // a posição relativa como está; senão, é um nó "solto" e recebe o offset.
      const parentNewId = cn.parent_client_id ? idMap.get(cn.parent_client_id) : undefined;
      const position = parentNewId
        ? { x: cn.position_x, y: cn.position_y }
        : { x: cn.position_x + OFFSET, y: cn.position_y + OFFSET };
      return {
        id: newId,
        type: 'shape',
        position,
        ...(parentNewId ? { parentId: parentNewId } : {}),
        data: {
          label: cn.label,
          shape: cn.shape,
          width: w,
          height: h,
          style: cn.style ?? null,
          descricao: cn.descricao ?? null,
          responsavelLabel: null,
          departamentoLabel: null,
          apiNode: undefined,
          onLabelChange: handleLabelChange,
          onEditEnd: handleEditEnd,
          readonly: !isAdmin,
        } as ShapeNodeData,
        width: w,
        height: h,
        style: { width: w, height: h },
        selected: true,
      };
    });
    const newEdges: Edge[] = (parsed.edges || [])
      .map((ce): Edge | null => {
        const src = idMap.get(ce.source);
        const tgt = idMap.get(ce.target);
        if (!src || !tgt) return null;
        return {
          id: cuid(),
          source: src,
          target: tgt,
          sourceHandle: ce.sourceHandle ?? undefined,
          targetHandle: ce.targetHandle ?? undefined,
          type: 'default',
          markerEnd: { type: MarkerType.ArrowClosed },
          data: { apiEdge: undefined },
        };
      })
      .filter((e): e is Edge => e !== null);

    setNodes((prev) => orderContainersFirst([...prev.map((n) => ({ ...n, selected: false })), ...newNodes]));
    setEdges((prev) => [...prev, ...newEdges]);
    setSelectedNodeIds(newNodes.map((n) => n.id));
    markDirty();
  }, [captureSnapshot, handleEditEnd, handleLabelChange, isAdmin, markDirty, setEdges, setNodes]);

  const cutSelection = useCallback(() => {
    if (selectedNodeIds.length === 0 || !isAdmin) return;
    copySelection();
    handleDeleteSelection();
  }, [copySelection, handleDeleteSelection, isAdmin, selectedNodeIds]);

  const handleAlign = useCallback(
    (dir: AlignDirection) => {
      if (selectedNodeIds.length === 0 || !isAdmin) return;

      // Com 1 forma selecionada, os botões de alinhamento editam o alinhamento do
      // texto dentro da forma (não há o que "alinhar" de posição sozinho).
      if (selectedNodeIds.length === 1) {
        const node = nodes.find((n) => n.id === selectedNodeIds[0]);
        if (!node) return;
        const d = node.data as ShapeNodeData;
        if (dir === 'left' || dir === 'center' || dir === 'right') {
          handlePanelChange({ style: { ...(d.style || {}), text_align: dir } });
        } else {
          handlePanelChange({ style: { ...(d.style || {}), text_valign: dir } });
        }
        return;
      }

      if (selectedNodeIds.length < 2) return;
      captureSnapshot();
      const targets = nodes.filter((n) => selectedNodeIds.includes(n.id));
      const widths = targets.map((n) => (typeof n.width === 'number' ? n.width : n.measured?.width ?? 180));
      const heights = targets.map((n) => (typeof n.height === 'number' ? n.height : n.measured?.height ?? 80));

      let getNewX: (n: Node, idx: number) => number = (n) => n.position.x;
      let getNewY: (n: Node, idx: number) => number = (n) => n.position.y;

      if (dir === 'left') {
        const x = Math.min(...targets.map((n) => n.position.x));
        getNewX = () => x;
      } else if (dir === 'right') {
        const right = Math.max(...targets.map((n, i) => n.position.x + widths[i]));
        getNewX = (_n, i) => right - widths[i];
      } else if (dir === 'center') {
        const center = targets.reduce((sum, n, i) => sum + n.position.x + widths[i] / 2, 0) / targets.length;
        getNewX = (_n, i) => center - widths[i] / 2;
      } else if (dir === 'top') {
        const y = Math.min(...targets.map((n) => n.position.y));
        getNewY = () => y;
      } else if (dir === 'bottom') {
        const bottom = Math.max(...targets.map((n, i) => n.position.y + heights[i]));
        getNewY = (_n, i) => bottom - heights[i];
      } else if (dir === 'middle') {
        const middle = targets.reduce((sum, n, i) => sum + n.position.y + heights[i] / 2, 0) / targets.length;
        getNewY = (_n, i) => middle - heights[i] / 2;
      }

      const indexById = new Map(targets.map((n, i) => [n.id, i] as const));
      setNodes((prev) =>
        prev.map((n) => {
          const idx = indexById.get(n.id);
          if (idx === undefined) return n;
          return { ...n, position: { x: getNewX(n, idx), y: getNewY(n, idx) } };
        }),
      );
      markDirty();
    },
    [captureSnapshot, handlePanelChange, isAdmin, markDirty, nodes, selectedNodeIds, setNodes],
  );

  const handleDistribute = useCallback(
    (axis: 'horizontal' | 'vertical') => {
      if (selectedNodeIds.length < 3 || !isAdmin) return;
      captureSnapshot();
      const targets = nodes.filter((n) => selectedNodeIds.includes(n.id));
      const sizeOf = (n: Node) =>
        axis === 'horizontal'
          ? typeof n.width === 'number'
            ? n.width
            : n.measured?.width ?? 180
          : typeof n.height === 'number'
            ? n.height
            : n.measured?.height ?? 80;
      const posOf = (n: Node) => (axis === 'horizontal' ? n.position.x : n.position.y);
      const sorted = [...targets].sort((a, b) => posOf(a) - posOf(b));
      const start = posOf(sorted[0]);
      const end = posOf(sorted[sorted.length - 1]) + sizeOf(sorted[sorted.length - 1]);
      const totalSize = sorted.reduce((sum, n) => sum + sizeOf(n), 0);
      const gap = (end - start - totalSize) / (sorted.length - 1);
      let cursor = start;
      const newPos = new Map<string, number>();
      for (const n of sorted) {
        newPos.set(n.id, cursor);
        cursor += sizeOf(n) + gap;
      }
      setNodes((prev) =>
        prev.map((n) => {
          const v = newPos.get(n.id);
          if (v === undefined) return n;
          return {
            ...n,
            position: axis === 'horizontal' ? { x: v, y: n.position.y } : { x: n.position.x, y: v },
          };
        }),
      );
      markDirty();
    },
    [captureSnapshot, isAdmin, markDirty, nodes, selectedNodeIds, setNodes],
  );

  const handleAutoLayout = useCallback(() => {
    if (!isAdmin) return;
    captureSnapshot();
    const { nodes: laid } = applyAutoLayout(nodes, edges, { direction: 'LR' });
    setNodes(laid);
    markDirty();
    setTimeout(() => fitView({ duration: 300, padding: 0.2 }), 50);
  }, [captureSnapshot, edges, fitView, isAdmin, markDirty, nodes, setNodes]);

  const handleFitView = useCallback(() => {
    fitView({ duration: 250, padding: 0.2 });
  }, [fitView]);

  const handleShare = useCallback(async () => {
    if (!fluxograma) return;
    try {
      if (fluxograma.visibilidade === 'publico_link' && fluxograma.share_token) {
        const url = `${window.location.origin}/f/${fluxograma.share_token}`;
        await navigator.clipboard.writeText(url).catch(() => undefined);
        toast.success('Link público copiado');
        return;
      }
      const result = await fluxogramasService.generateShareLink(fluxograma.id);
      if (result.success && result.data.share_token) {
        const url = `${window.location.origin}/f/${result.data.share_token}`;
        await navigator.clipboard.writeText(url).catch(() => undefined);
        setFluxograma((prev) =>
          prev
            ? { ...prev, share_token: result.data.share_token, visibilidade: 'publico_link' }
            : prev,
        );
        toast.success('Link público gerado e copiado');
      } else {
        toast.error(result.error || 'Erro ao gerar link público');
      }
    } catch (_) {
      toast.error('Erro ao gerar link público');
    }
  }, [fluxograma]);

  const handleOpenPermissoes = useCallback(async () => {
    if (!fluxograma || !isAdmin) return;
    setPermissoesLoading(true);
    try {
      const [permissoesRes, usuariosRes] = await Promise.all([
        fluxogramasService.getPermissoes(fluxograma.id),
        usuarioService.getByEmpresa(fluxograma.empresa_id),
      ]);

      if (!permissoesRes.success) {
        toast.error(permissoesRes.error || 'Erro ao carregar permissões');
        return;
      }
      if (!usuariosRes.success) {
        toast.error(usuariosRes.error || 'Erro ao carregar usuários');
        return;
      }

      setPermissoes(permissoesRes.data || []);
      setUsuariosPermissoes(usuariosRes.data || []);
      setPermissoesOpen(true);
    } catch (_) {
      toast.error('Erro ao carregar permissões');
    } finally {
      setPermissoesLoading(false);
    }
  }, [fluxograma, isAdmin]);

  const handleChangePermissao = useCallback((usuarioId: string, role: 'none' | 'owner' | 'editor' | 'viewer') => {
    setPermissoes((current) => {
      const remaining = current.filter((item) => item.usuario_id !== usuarioId);
      if (role === 'none') return remaining;

      const usuario = usuariosPermissoes.find((item) => item.id === usuarioId);
      return [
        ...remaining,
        {
          id: `${usuarioId}-${role}`,
          fluxograma_id: fluxograma?.id || '',
          usuario_id: usuarioId,
          role,
          usuario: usuario ? { id: usuario.id, nome: usuario.nome, email: usuario.email } : undefined,
        },
      ];
    });
  }, [fluxograma?.id, usuariosPermissoes]);

  const handleSavePermissoes = useCallback(async () => {
    if (!fluxograma) return;
    try {
      setPermissoesLoading(true);
      const payload = permissoes.map((item) => ({ usuario_id: item.usuario_id, role: item.role }));
      const result = await fluxogramasService.updatePermissoes(fluxograma.id, payload);
      if (result.success) {
        setPermissoes(result.data || []);
        setPermissoesOpen(false);
        toast.success('Permissões atualizadas');
      } else {
        toast.error(result.error || 'Erro ao atualizar permissões');
      }
    } catch (_) {
      toast.error('Erro ao atualizar permissões');
    } finally {
      setPermissoesLoading(false);
    }
  }, [fluxograma, permissoes]);

  const handleRevoke = useCallback(async () => {
    if (!fluxograma?.share_token) return;
    try {
      const result = await fluxogramasService.revokeShareLink(fluxograma.id);
      if (result.success) {
        setFluxograma((prev) => (prev ? { ...prev, share_token: null, visibilidade: 'empresa' } : prev));
        toast.success('Link público revogado');
      } else {
        toast.error(result.error || 'Erro ao revogar link');
      }
    } catch (_) {
      toast.error('Erro ao revogar link');
    }
  }, [fluxograma]);

  const handleExportPng = useCallback(async () => {
    const viewport = reactFlowWrapper.current?.querySelector('.react-flow__viewport') as HTMLElement | null;
    const target = (reactFlowWrapper.current?.querySelector('.react-flow') as HTMLElement | null) || viewport;
    if (!target) {
      toast.error('Nada para exportar');
      return;
    }
    try {
      await exportElementAsPng(target, {
        filename: `${(fluxograma?.nome || 'fluxograma').replace(/\s+/g, '_')}.png`,
        backgroundColor: '#ffffff',
      });
      toast.success('PNG exportado');
    } catch (_) {
      toast.error('Erro ao exportar PNG');
    }
  }, [fluxograma]);

  const handleExportPdf = useCallback(async () => {
    const target = reactFlowWrapper.current?.querySelector('.react-flow') as HTMLElement | null;
    if (!target) {
      toast.error('Nada para exportar');
      return;
    }
    try {
      await exportElementAsPdf(target, {
        filename: `${(fluxograma?.nome || 'fluxograma').replace(/\s+/g, '_')}.pdf`,
        backgroundColor: '#ffffff',
      });
      toast.success('PDF exportado');
    } catch (_) {
      toast.error('Erro ao exportar PDF');
    }
  }, [fluxograma]);

  const handleToggleSnap = useCallback(async () => {
    const next = !snapEnabled;
    setSnapEnabled(next);
    if (!fluxograma) return;

    const result = await fluxogramasService.update(fluxograma.id, {
      canvas_config: {
        snap_to_grid: next,
        grid_size: fluxograma.canvas_config?.grid_size ?? 16,
        background: fluxograma.canvas_config?.background ?? 'dots',
      },
    });
    if (result.success && result.data) {
      setFluxograma(result.data);
    } else {
      setSnapEnabled(!next);
      toast.error(result.error || 'Erro ao salvar configuração de encaixe na grade');
    }
  }, [fluxograma, snapEnabled]);

  const handleGridSizeChange = useCallback(
    async (size: number) => {
      if (!fluxograma) return;
      const nextConfig = {
        snap_to_grid: fluxograma.canvas_config?.snap_to_grid ?? snapEnabled,
        grid_size: size,
        background: fluxograma.canvas_config?.background ?? 'dots',
      };
      const result = await fluxogramasService.update(fluxograma.id, { canvas_config: nextConfig });
      if (result.success && result.data) {
        setFluxograma(result.data);
      } else {
        toast.error(result.error || 'Erro ao salvar tamanho da grade');
      }
    },
    [fluxograma, snapEnabled],
  );

  // ============ Menu de contexto ============
  const selectAll = useCallback(() => {
    setNodes((prev) => prev.map((n) => ({ ...n, selected: true })));
    setSelectedNodeIds(nodes.map((n) => n.id));
  }, [nodes, setNodes]);

  const openNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      e.preventDefault();
      if (!isAdmin) return;
      setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === node.id })));
      setSelectedNodeIds([node.id]);
      setSelectedEdgeIds([]);
      setCtxMenu({ type: 'node', id: node.id, x: e.clientX, y: e.clientY });
    },
    [isAdmin, setNodes],
  );

  const openEdgeContextMenu = useCallback(
    (e: React.MouseEvent, edge: Edge) => {
      e.preventDefault();
      if (!isAdmin) return;
      setSelectedEdgeIds([edge.id]);
      setSelectedNodeIds([]);
      setCtxMenu({ type: 'edge', id: edge.id, x: e.clientX, y: e.clientY });
    },
    [isAdmin],
  );

  const openPaneContextMenu = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      if (!isAdmin) return;
      setCtxMenu({ type: 'pane', id: null, x: e.clientX, y: e.clientY });
    },
    [isAdmin],
  );

  const buildContextItems = useCallback(
    (ctx: CtxTarget): Array<CtxItem | 'separator'> => {
      if (ctx.type === 'node') {
        return [
          { label: 'Duplicar', shortcut: '⌘D', onClick: handleDuplicateSelection },
          { label: 'Copiar', shortcut: '⌘C', onClick: copySelection },
          { label: 'Colar', shortcut: '⌘V', onClick: pasteClipboard },
          'separator',
          { label: 'Trazer à frente', onClick: () => handleReorder('front') },
          { label: 'Enviar para trás', onClick: () => handleReorder('back') },
          'separator',
          { label: 'Excluir', shortcut: 'Del', onClick: handleDeleteSelection, danger: true },
        ];
      }
      if (ctx.type === 'edge') {
        return [
          { label: 'Inverter direção', onClick: () => handleReverseEdge(ctx.id ?? undefined) },
          'separator',
          {
            label: 'Excluir',
            shortcut: 'Del',
            onClick: () => ctx.id && handleDeleteEdgeById(ctx.id),
            danger: true,
          },
        ];
      }
      return [
        { label: 'Colar', shortcut: '⌘V', onClick: pasteClipboard },
        { label: 'Selecionar tudo', shortcut: '⌘A', onClick: selectAll },
        'separator',
        { label: 'Organizar automaticamente', onClick: handleAutoLayout },
        { label: 'Enquadrar', onClick: handleFitView },
      ];
    },
    [
      copySelection,
      handleAutoLayout,
      handleDeleteEdgeById,
      handleDeleteSelection,
      handleDuplicateSelection,
      handleFitView,
      handleReorder,
      handleReverseEdge,
      pasteClipboard,
      selectAll,
    ],
  );

  // ============ Tela cheia ============
  const toggleFullscreen = useCallback(async () => {
    const element = workspaceRef.current;
    if (!element) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
      return;
    }
    await element.requestFullscreen?.().catch(() => undefined);
  }, []);

  // ============ Busca no canvas ============
  const normalizeSearchText = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const searchMatches = useMemo(() => {
    const query = normalizeSearchText(searchQuery.trim());
    if (!query) return [] as Node[];
    return nodes.filter((node) => {
      const label = (node.data as ShapeNodeData | undefined)?.label || '';
      return normalizeSearchText(label).includes(query);
    });
  }, [nodes, searchQuery]);

  const focusSearchResult = useCallback(
    (index: number) => {
      const node = searchMatches[index];
      if (!node) return;
      const width = typeof node.width === 'number' ? node.width : 160;
      const height = typeof node.height === 'number' ? node.height : 80;
      setCenter(node.position.x + width / 2, node.position.y + height / 2, { zoom: 1.1, duration: 400 });
      setNodes((current) => current.map((n) => ({ ...n, selected: n.id === node.id })));
      setSelectedNodeIds([node.id]);
      setSelectedEdgeIds([]);
    },
    [searchMatches, setCenter, setNodes],
  );

  const handleSearchNavigate = useCallback(
    (direction: 1 | -1) => {
      if (searchMatches.length === 0) return;
      const nextIndex = (searchIndex + direction + searchMatches.length) % searchMatches.length;
      setSearchIndex(nextIndex);
      focusSearchResult(nextIndex);
    },
    [focusSearchResult, searchIndex, searchMatches.length],
  );

  // Refs (não reativos) para acessar a busca/foco mais recentes sem incluir
  // `searchMatches`/`focusSearchResult` nas deps do efeito abaixo — ambos mudam de
  // referência a cada alteração de `nodes`, e `focusSearchResult` chama `setNodes`,
  // o que recriaria o efeito indefinidamente (mesma classe de loop já corrigida
  // no wiring do onLabelChange).
  const searchMatchesRef = useRef(searchMatches);
  useEffect(() => {
    searchMatchesRef.current = searchMatches;
  }, [searchMatches]);
  const focusSearchResultRef = useRef(focusSearchResult);
  useEffect(() => {
    focusSearchResultRef.current = focusSearchResult;
  }, [focusSearchResult]);

  useEffect(() => {
    setSearchIndex(0);
    if (searchQuery.trim() && searchMatchesRef.current.length > 0) {
      focusSearchResultRef.current(0);
    }
  }, [searchQuery]);

  // ============ Atalhos de teclado ============
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      const editingText =
        tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable);
      if (editingText) return;

      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;

      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.key.toLowerCase() === 'z' && e.shiftKey) || e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
      } else if (e.key.toLowerCase() === 'd') {
        e.preventDefault();
        handleDuplicateSelection();
      } else if (e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setNodes((prev) => prev.map((n) => ({ ...n, selected: true })));
        setSelectedNodeIds((_) => nodes.map((n) => n.id));
      } else if (e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelection();
      } else if (e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteClipboard();
      } else if (e.key.toLowerCase() === 'x') {
        e.preventDefault();
        cutSelection();
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSaveNow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [copySelection, cutSelection, handleDuplicateSelection, handleRedo, handleSaveNow, handleUndo, nodes, pasteClipboard, setNodes]);

  const headerStatus = useMemo(() => {
    if (saveBlockedByConflict) {
      return <span className="text-xs text-destructive">Conflito de versão: recarregue para continuar salvando</span>;
    }
    if (saving) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Salvando…
        </span>
      );
    }
    if (dirty) return <span className="text-xs text-amber-600">Alterações não salvas</span>;
    if (lastSavedAt) {
      return (
        <span className="text-xs text-muted-foreground">
          Salvo às{' '}
          {lastSavedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
        </span>
      );
    }
    return null;
  }, [dirty, lastSavedAt, saveBlockedByConflict, saving]);

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[calc(100vh-200px)] w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!fluxograma) return null;

  return (
    <AppLayout>
      <div ref={workspaceRef} className="flex flex-col h-[calc(100vh-64px)] overflow-y-auto bg-background">
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-muted/20 px-2 py-1 mx-4 mt-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => navigate('/fluxogramas')}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Voltar para Fluxogramas</TooltipContent>
          </Tooltip>

          {isRenamingTitle ? (
            <Input
              autoFocus
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              onBlur={commitRename}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitRename();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setIsRenamingTitle(false);
                }
              }}
              className="h-7 w-56 text-sm font-medium"
            />
          ) : (
            <button
              type="button"
              className={cn(
                'truncate rounded px-1 -mx-1 text-sm font-semibold',
                isAdmin && 'cursor-text hover:bg-muted/60 hover:text-foreground',
              )}
              title={isAdmin ? 'Clique para renomear' : undefined}
              onClick={() => {
                if (!isAdmin) return;
                setRenameValue(fluxograma.nome);
                setIsRenamingTitle(true);
              }}
            >
              {fluxograma.nome}
            </button>
          )}
          <Badge variant="outline" className="text-xs">
            {tipoLabel[fluxograma.tipo]}
          </Badge>
          {headerStatus}

          <div className="mx-1 h-5 w-px shrink-0 bg-border" />

          {!isMobile && (
            <div className="flex items-center rounded-md border border-border/70 bg-background p-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={interactionMode === 'select' ? 'secondary' : 'ghost'}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setInteractionMode('select')}
                  >
                    <MousePointer2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Selecionar (arraste para marcar itens)</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={interactionMode === 'pan' ? 'secondary' : 'ghost'}
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setInteractionMode('pan')}
                  >
                    <Hand className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mover (arraste para navegar pelo canvas)</TooltipContent>
              </Tooltip>
            </div>
          )}

          {isAdmin && (
            <Select
              value={String(fluxograma.canvas_config?.grid_size ?? 16)}
              onValueChange={(value) => handleGridSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[92px] text-xs" title="Tamanho da grade">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="8">Grade 8px</SelectItem>
                <SelectItem value="16">Grade 16px</SelectItem>
                <SelectItem value="24">Grade 24px</SelectItem>
                <SelectItem value="32">Grade 32px</SelectItem>
              </SelectContent>
            </Select>
          )}

          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (searchMatches.length === 0) return;
                  handleSearchNavigate(event.shiftKey ? -1 : 1);
                }
                if (event.key === 'Escape') {
                  setSearchQuery('');
                  event.currentTarget.blur();
                }
              }}
              placeholder="Buscar (Ctrl+K)"
              className="h-8 w-[170px] rounded-md border-border/80 bg-background pl-8 text-sm"
            />
          </div>
          {searchQuery && (
            <>
              <span className="text-xs tabular-nums text-muted-foreground">
                {searchMatches.length > 0 ? `${searchIndex + 1}/${searchMatches.length}` : '0'}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => handleSearchNavigate(-1)}
                disabled={searchMatches.length === 0}
                title="Resultado anterior"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => handleSearchNavigate(1)}
                disabled={searchMatches.length === 0}
                title="Próximo resultado"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleFullscreen}>
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Tela cheia</TooltipContent>
          </Tooltip>

          <div className="ml-auto flex items-center gap-2">
            <Button onClick={handleSaveNow} disabled={saving || saveBlockedByConflict} size="sm">
              <Save className="h-4 w-4 mr-2" />
              Salvar
            </Button>
          </div>
        </div>

        <FluxogramaToolbar
          canUndo={canUndo}
          canRedo={canRedo}
          selectionCount={selectedNodeIds.length}
          snapEnabled={snapEnabled}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onDuplicateSelection={handleDuplicateSelection}
          onDeleteSelection={handleDeleteSelection}
          onAlign={handleAlign}
          onDistribute={handleDistribute}
          onAutoLayout={handleAutoLayout}
          onFitView={handleFitView}
          paletteOpen={paletteOpen}
          onTogglePalette={isAdmin && !telaCelular ? alternarPaleta : undefined}
          onToggleSnap={handleToggleSnap}
          disabled={!isAdmin}
        >
          <div className="mx-1 h-5 w-px shrink-0 bg-border" />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleShare}>
                <Share2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Compartilhar</TooltipContent>
          </Tooltip>
          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleOpenPermissoes} disabled={permissoesLoading}>
                  <Users className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Permissões</TooltipContent>
            </Tooltip>
          )}
          {fluxograma.share_token && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleRevoke}>
                  <Link2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Revogar link</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleExportPng}>
                <FileImage className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Exportar PNG</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleExportPdf}>
                <FileText className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Exportar PDF</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setHistoricoOpen(true)}>
                <History className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Histórico</TooltipContent>
          </Tooltip>
        </FluxogramaToolbar>

        <div
          ref={canvasAreaRef}
          className={canvasHeight ? 'flex min-h-0 relative shrink-0' : 'flex-1 flex min-h-0 relative'}
          style={canvasHeight ? { height: canvasHeight } : undefined}
        >
          {isAdmin && paletteOpen && <ShapesPalette onDragStart={setDraggingShape} />}

          {isAdmin && telaCelular && (
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-2 left-2 z-10 shadow"
              onClick={() => setPaletteOpen((v) => !v)}
              title={paletteOpen ? 'Ocultar formas' : 'Mostrar formas'}
            >
              {paletteOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </Button>
          )}

          <div
            className="flex-1 bg-muted/30 relative"
            ref={reactFlowWrapper}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {ligacaoAlvo && (
              <style>{estiloLigacao(ligacaoAlvo.alvoId, ligacaoAlvo.arrastadoId, ligacaoAlvo.seloEmBaixo)}</style>
            )}
            {draggingShape && dragCursor && (
              <DragGhost shape={draggingShape} cursor={dragCursor} zoom={getZoom()} />
            )}
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={handleNodesChangeWrapped}
              onEdgesChange={handleEdgesChangeWrapped}
              onConnect={handleConnect}
              onConnectStart={handleConnectStart}
              onConnectEnd={handleConnectEnd}
              onNodeDragStart={handleNodeDragStart}
              onNodeDrag={handleNodeDrag}
              onNodeDragStop={handleNodeDragStop}
              onSelectionChange={handleSelectionChange}
              onSelectionStart={() => {
                isBoxSelectingRef.current = true;
              }}
              onSelectionEnd={() => {
                isBoxSelectingRef.current = false;
              }}
              onNodeContextMenu={openNodeContextMenu}
              onEdgeContextMenu={openEdgeContextMenu}
              onPaneContextMenu={openPaneContextMenu}
              fitView
              snapToGrid={snapEnabled}
              snapGrid={[fluxograma.canvas_config?.grid_size ?? 16, fluxograma.canvas_config?.grid_size ?? 16]}
              connectionRadius={30}
              selectNodesOnDrag={false}
              multiSelectionKeyCode={['Shift', 'Control', 'Meta']}
              deleteKeyCode={isAdmin ? ['Delete', 'Backspace'] : null}
              selectionOnDrag={!isMobile && interactionMode === 'select'}
              panOnDrag={isMobile ? true : interactionMode === 'select' ? [1] : true}
            >
              <Background
                variant={
                  fluxograma.canvas_config?.background === 'lines'
                    ? BackgroundVariant.Lines
                    : fluxograma.canvas_config?.background === 'none'
                      ? undefined
                      : BackgroundVariant.Dots
                }
                gap={fluxograma.canvas_config?.grid_size ?? 16}
              />
              <Controls position="bottom-left" />
              <MiniMap
                position="bottom-right"
                pannable
                zoomable
                nodeColor={(node) => {
                  const d = node.data as ShapeNodeData;
                  const meta = SHAPE_META[d.shape] ?? SHAPE_META.process;
                  return d.style?.border_color || meta.defaultColor;
                }}
                maskColor="rgba(68, 64, 60, 0.12)"
              />
              <AlignmentGuides horizontal={helperLineH} vertical={helperLineV} />
              {isAdmin && (
                <NodeQuickToolbar
                  nodeIds={selectedNodeIds}
                  onDuplicate={handleDuplicateSelection}
                  onDelete={handleDeleteSelection}
                  onColor={handleQuickColor}
                  onBringToFront={() => handleReorder('front')}
                  onSendToBack={() => handleReorder('back')}
                />
              )}
            </ReactFlow>
          </div>

          {selectedApiNode && (
            <NodePropertiesPanel
              node={selectedApiNode}
              pessoas={pessoas}
              departamentos={departamentos}
              projetos={projetos}
              onChange={handlePanelChange}
              onClose={() => setSelectedNodeIds([])}
              readonly={!isAdmin}
            />
          )}

          {!selectedApiNode && selectedApiEdge && (
            <EdgePropertiesPanel
              edge={selectedApiEdge}
              onChange={handleEdgePanelChange}
              onReverse={handleReverseEdge}
              onClose={() => setSelectedEdgeIds([])}
              readonly={!isAdmin}
            />
          )}

          <div
            role="separator"
            aria-orientation="horizontal"
            title="Arraste para redimensionar o canvas"
            onMouseDown={handleCanvasResizeStart}
            className="group absolute inset-x-0 bottom-0 z-30 flex h-3 cursor-ns-resize items-center justify-center"
          >
            <div className="h-1 w-16 rounded-full bg-border transition-colors group-hover:bg-primary/60" />
          </div>
        </div>
      </div>

      <CanvasContextMenu ctx={ctxMenu} onClose={() => setCtxMenu(null)} buildItems={buildContextItems} />

      <HistoricoDrawer
        open={historicoOpen}
        onOpenChange={setHistoricoOpen}
        fluxogramaId={fluxograma.id}
      />

      <Dialog open={permissoesOpen} onOpenChange={setPermissoesOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Permissões do fluxograma</DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] space-y-3 overflow-y-auto">
            <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
              Sem permissões explícitas, o fluxograma segue acessível como hoje. Quando você salvar permissões aqui, elas passam a controlar leitura e edição para usuários comuns.
            </div>
            {usuariosPermissoes.map((usuario) => {
              const selectedRole = permissoes.find((item) => item.usuario_id === usuario.id)?.role || 'none';
              return (
                <div key={usuario.id} className="grid items-center gap-3 rounded-lg border p-4 md:grid-cols-[1fr_220px]">
                  <div>
                    <div className="font-medium">{usuario.nome}</div>
                    <div className="text-sm text-muted-foreground">{usuario.email}</div>
                  </div>
                  <div>
                    <Label className="sr-only" htmlFor={`perm-${usuario.id}`}>Permissão</Label>
                    <Select value={selectedRole} onValueChange={(value: 'none' | 'owner' | 'editor' | 'viewer') => handleChangePermissao(usuario.id, value)}>
                      <SelectTrigger id={`perm-${usuario.id}`}>
                        <SelectValue placeholder="Sem permissão explícita" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem permissão explícita</SelectItem>
                        <SelectItem value="viewer">Leitor</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="owner">Dono</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissoesOpen(false)} disabled={permissoesLoading}>
              Cancelar
            </Button>
            <Button onClick={handleSavePermissoes} disabled={permissoesLoading}>
              {permissoesLoading ? 'Salvando…' : 'Salvar permissões'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveConflictOpen} onOpenChange={setSaveConflictOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Fluxograma desatualizado</DialogTitle>
            <DialogDescription>
              Este fluxograma foi alterado por outra pessoa ou em outra aba. Para evitar sobrescrever mudanças mais novas, o salvamento foi pausado até você recarregar a versão atual.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveConflictOpen(false)}>
              Revisar antes
            </Button>
            <Button onClick={handleReloadAfterConflict}>
              Recarregar versão atual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default function FluxogramaEditor() {
  return (
    <ReactFlowProvider>
      <FluxogramaEditorInner />
    </ReactFlowProvider>
  );
}
